import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IntlShape } from 'react-intl';

import { IRoomsDAO } from '../../../../../api/rooms.dao';
import { IEventDetailsDTO } from '../../../../../api/events.dao';

import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IAlertsService } from '../../../../../infrastructure/alerts';

import { EventExecutionRuntime } from '../../../../../domain/jobs/implementations/event-execution-runtime';
import { JobType } from '../../../../../api/jobs.dao';
import { GenerateRoomsFormDataVM } from './_generate-rooms-modal/_generate-rooms-form/generate-rooms-form-data.viewmodel';

class GenerateRoomsVM {
    @mobx.observable
    private isFormShown: boolean;

    @mobx.observable
    private saving: boolean;

    @mobx.observable
    private generateRoomsFormDataVM: GenerateRoomsFormDataVM;


    constructor(
        private readonly event: Readonly<IEventDetailsDTO>,
        private readonly intlProvider: Readonly<IntlShape>,
        private readonly onRoomGenerated: () => void,
        private readonly roomsDAO: IRoomsDAO = IoC.Container.get(IRoomsDAO),
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly jobsPipeline: EventExecutionRuntime = IoC.Container.get(EventExecutionRuntime)
    ) {
        this.initFormDataVM();
        this.isFormShown = false;
        this.saving = false;
    }

    public get intl(): IntlShape { return this.intlProvider; }

    public isSaving() {
        return this.saving;
    }

    public getEvent() {
        return this.event;
    }

    @mobx.action
    public generateRooms = async (): Promise<void> => {

        if (this.saving || !this.generateRoomsFormDataVM.checkForm()) {
            return;
        }

        this.saving = true;

        const job = this.jobsPipeline.getJobByType(JobType.GenerateRooms);
        job.onComplete(this.onRoomGenerated);

        try {
            await job.trigger(this.generateRoomsFormDataVM.toApiDTO());
            mobx.runInAction(() => {
                this.saving = false;
                this.hideAndResetForm();
            });
        } catch {
            mobx.runInAction(() => {
                this.saving = false;
                if (!this.isFormShown) this.showForm();
            });
        }
    };

    public getFormDataVM() {
        return this.generateRoomsFormDataVM;
    }

    @mobx.action
    public showForm = () => {
        this.isFormShown = true;
    };

    public hideAndResetForm = () => {
        this.hideForm();
        this.initFormDataVM();
    };


    public getIsFormShown = () => {
        return this.isFormShown;
    };

    @mobx.action
    private initFormDataVM() {
        this.generateRoomsFormDataVM = new GenerateRoomsFormDataVM(this.event.startDateTime, this.event.endDateTime, this.event.timeZone);
    }

    @mobx.action
    private hideForm() {
        if (!this.saving) {
            this.isFormShown = false;
        }
    }
}

export { GenerateRoomsVM };
